/**
 * VehicleProfileEditor — modal for the Power panel's vehicle parameters.
 *
 * Edits mass / drag / frontal area / rolling resistance (plus an Advanced
 * section for the smoothing window and road-grade toggle). Changes apply live to
 * the Power panel via onChange; "Save to car" persists them to a reusable
 * vehicle profile auto-matched by the session's Vehicle name on future loads.
 *
 * Props
 * -----
 * value       : current params object
 *               { vehicle_id, vehicle_name, mass_kg, cd, frontal_area_m2, crr,
 *                 smoothing_m, include_grade, saved, preset, preset_params }
 * onChange    : (params) => void   live-apply (debounced internally)
 * onClose     : () => void
 * sessionId   : string|undefined   when present, enables editing the session's
 *               Vehicle name / Driver name (session metadata, distinct from the
 *               physics "Save to car"). Omitted when reused outside a session
 *               (e.g. the Settings car manager).
 * racer       : string|undefined   current session driver name
 * onMetaSaved : ({ vehicle, racer }) => void   notify parent after a meta save
 */
import { useState, useRef, useEffect, useCallback } from 'react';
import { createVehicle, patchCar, patchSessionMeta } from '../../services/api';
import styles from './FormulaEditor.module.css';

const APPLY_DEBOUNCE_MS = 350;
const KG_PER_LB = 0.45359237;
const MASS_UNIT_KEY = 'vehicle.massUnit';

const FIELDS = [
  {
    key: 'cd', label: 'Drag coeff (Cd)', step: 0.01, hint: 'Typically 0.30–0.40',
    tip: 'Aerodynamic drag coefficient — how slippery the body shape is (dimensionless). '
       + 'Lower is slicker. Typical road/track cars 0.30–0.40; cars making real downforce '
       + 'run higher. With frontal area it sets aero drag, which dominates power at high speed.',
  },
  {
    key: 'frontal_area_m2', label: 'Frontal area (m²)', step: 0.05, hint: 'Typically 1.9–2.3',
    tip: "The car's cross-section facing the wind, in square metres (≈ track width × height × 0.85). "
       + 'Most cars are 1.9–2.3 m². Combined with Cd it determines aerodynamic drag.',
  },
];

export default function VehicleProfileEditor({ value, onChange, onClose, sessionId, racer, onMetaSaved }) {
  const [form, setForm] = useState(() => ({
    crr: 0.013,
    smoothing_m: 80,
    include_grade: true,
    ...value,
  }));

  // Session identity (Vehicle name / Driver name) — persisted as session metadata,
  // separate from the physics "Save to car" flow below. Only editable with a session.
  const [vehicleName, setVehicleName] = useState(() =>
    value?.vehicle_name && value.vehicle_name !== 'Unknown' ? value.vehicle_name : '');
  const [driverName, setDriverName] = useState(() =>
    racer && racer !== 'Unknown' ? racer : '');
  const [savingMeta, setSavingMeta] = useState(false);
  const [metaMsg, setMetaMsg] = useState(null);
  const [metaError, setMetaError] = useState(null);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [massUnit, setMassUnit] = useState(() => {
    try { return localStorage.getItem(MASS_UNIT_KEY) === 'lb' ? 'lb' : 'kg'; }
    catch { return 'kg'; }
  });
  const [saving, setSaving] = useState(false);
  const [savedMsg, setSavedMsg] = useState(value?.saved ? 'Loaded from saved car' : null);
  const [error, setError] = useState(null);

  const applyTimer = useRef(null);

  // Live-apply changes to the panel (debounced) so the curve rescales as you type.
  const scheduleApply = useCallback((next) => {
    if (applyTimer.current) clearTimeout(applyTimer.current);
    applyTimer.current = setTimeout(() => onChange?.(next), APPLY_DEBOUNCE_MS);
  }, [onChange]);

  useEffect(() => () => { if (applyTimer.current) clearTimeout(applyTimer.current); }, []);

  const setField = (key, raw) => {
    const num = raw === '' ? '' : Number(raw);
    setForm(prev => {
      const next = { ...prev, [key]: num };
      setSavedMsg(null);
      scheduleApply(next);
      return next;
    });
  };

  const setFlag = (key, val) => {
    setForm(prev => {
      const next = { ...prev, [key]: val };
      scheduleApply(next);
      return next;
    });
  };

  // Mass is always stored in kg; the input may be entered in kg or lb.
  const setMassFromDisplay = (raw) => {
    setForm(prev => {
      const kg = raw === '' ? '' : (massUnit === 'lb' ? Number(raw) * KG_PER_LB : Number(raw));
      const next = { ...prev, mass_kg: kg };
      setSavedMsg(null);
      scheduleApply(next);
      return next;
    });
  };

  const changeMassUnit = (u) => {
    setMassUnit(u);
    try { localStorage.setItem(MASS_UNIT_KEY, u); } catch { /* ignore */ }
  };

  const displayMass = (form.mass_kg === '' || form.mass_kg == null)
    ? ''
    : Math.round(massUnit === 'lb' ? form.mass_kg / KG_PER_LB : form.mass_kg);

  // Smoothing is a constant-time window anchored to the session's typical speed;
  // Preset (from the vehicle name) — a per-car starting point instead of one
  // generic default. Saved values always take precedence server-side.
  const presetApplied = !!form.preset_params && ['mass_kg', 'cd', 'frontal_area_m2']
    .every(k => Number(form[k]) === Number(form.preset_params[k]));
  const resetToPreset = () => {
    if (!form.preset_params) return;
    setForm(prev => {
      const { mass_kg, cd, frontal_area_m2 } = prev.preset_params;
      const next = { ...prev, mass_kg, cd, frontal_area_m2 };
      setSavedMsg(null);
      scheduleApply(next);
      return next;
    });
  };

  // surface the implied seconds so the metres value is meaningful.
  const typicalKmh = form.typical_speed_kmh;
  const impliedSec = (typicalKmh && form.smoothing_m)
    ? Number(form.smoothing_m) / (typicalKmh / 3.6)
    : null;

  // Saving is blocked for a session you don't own (the params belong to the
  // car's owner). A resolved `car_id` means we persist to the per-user car;
  // otherwise fall back to the legacy global filesystem profile.
  const canSave = form.car_id ? form.owned !== false : true;

  const handleSave = async () => {
    setError(null);
    setSaving(true);
    try {
      const params = {
        mass_kg: Number(form.mass_kg),
        cd: Number(form.cd),
        frontal_area_m2: Number(form.frontal_area_m2),
        crr: Number(form.crr),
        smoothing_m: Number(form.smoothing_m),
      };
      if (form.car_id) {
        await patchCar(form.car_id, params);
      } else {
        await createVehicle({
          vehicle_id: form.vehicle_id, vehicle_name: form.vehicle_name, ...params,
        });
      }
      setSavedMsg(`Saved to "${form.vehicle_name}"`);
    } catch (e) {
      setError(e.message || 'Failed to save vehicle');
    } finally {
      setSaving(false);
    }
  };

  const metaDirty =
    vehicleName.trim() !== (value?.vehicle_name && value.vehicle_name !== 'Unknown' ? value.vehicle_name : '') ||
    driverName.trim() !== (racer && racer !== 'Unknown' ? racer : '');

  const handleSaveMeta = async () => {
    setMetaError(null);
    setSavingMeta(true);
    try {
      const patch = { vehicle: vehicleName.trim(), racer: driverName.trim() };
      await patchSessionMeta(sessionId, patch);
      onMetaSaved?.(patch);
      setMetaMsg('Saved');
    } catch (e) {
      setMetaError(e.message || 'Failed to save');
    } finally {
      setSavingMeta(false);
    }
  };

  return (
    <div className={styles.overlay} onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className={styles.dialog}>
        <div className={styles.header}>
          <span className={styles.title}>Vehicle — {vehicleName || form.vehicle_name || 'Power params'}</span>
          <button className={styles.closeBtn} onClick={onClose}>✕</button>
        </div>

        <div className={styles.body}>
          {sessionId && (
            <>
              <label
                className={styles.field}
                title="The name shown for this session's vehicle. Edit if it imported blank or wrong."
              >
                <span className={styles.fieldLabel}>Vehicle name</span>
                <input
                  className={styles.input}
                  type="text"
                  value={vehicleName}
                  placeholder="Vehicle name"
                  onChange={e => { setVehicleName(e.target.value); setMetaMsg(null); }}
                />
              </label>
              <label
                className={styles.field}
                title="The driver for this session. Defaults to the uploader; edit for other drivers."
              >
                <span className={styles.fieldLabel}>Driver name</span>
                <input
                  className={styles.input}
                  type="text"
                  value={driverName}
                  placeholder="Driver name"
                  onChange={e => { setDriverName(e.target.value); setMetaMsg(null); }}
                />
              </label>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: -2, marginBottom: 4 }}>
                <button
                  className={styles.saveBtn}
                  onClick={handleSaveMeta}
                  disabled={savingMeta || !metaDirty}
                >{savingMeta ? 'Saving…' : 'Save name & driver'}</button>
                {metaMsg && <span className={styles.fieldLabel} style={{ opacity: 0.7, fontWeight: 400 }}>{metaMsg}</span>}
                {metaError && <span className={styles.errorMsg}>⚠ {metaError}</span>}
              </div>
            </>
          )}

          {form.preset && (
            <div
              className={styles.field}
              style={{ flexDirection: 'row', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}
              title={'These numbers came from the preset for this car (published curb mass '
                + '+ 80 kg driver + half a tank, stock drag figures). Adjust for your driver, '
                + 'fuel load and aero, then Save to car.'}
            >
              <span className={styles.fieldLabel} style={{ opacity: 0.7, fontWeight: 400 }}>
                {form.saved ? 'Preset available: ' : 'Starting from preset: '}<b>{form.preset}</b>
              </span>
              <button
                className={styles.chip}
                onClick={resetToPreset}
                disabled={!form.preset_params || presetApplied}
                title="Put the preset's mass, Cd and frontal area back in the fields"
              >Reset to preset</button>
            </div>
          )}

          <label
            className={styles.field}
            title={'All-up mass: car + driver + a typical fuel load. The single most '
              + 'important value — power scales directly with mass during acceleration. '
              + 'Enter in kg or lb (stored internally in kg).'}
          >
            <span className={styles.fieldLabel}>Mass</span>
            <div style={{ display: 'flex', gap: 6 }}>
              <input
                className={styles.input} style={{ flex: 1 }}
                type="number" step={massUnit === 'lb' ? 25 : 10}
                value={displayMass}
                onChange={e => setMassFromDisplay(e.target.value)}
              />
              <select
                className={styles.input} style={{ flex: '0 0 64px' }}
                value={massUnit}
                onChange={e => changeMassUnit(e.target.value)}
              >
                <option value="kg">kg</option>
                <option value="lb">lb</option>
              </select>
            </div>
            <span className={styles.fieldLabel} style={{ opacity: 0.6, fontWeight: 400 }}>Car + driver + fuel</span>
          </label>

          {FIELDS.map(({ key, label, step, hint, tip }) => (
            <label key={key} className={styles.field} title={tip}>
              <span className={styles.fieldLabel}>{label}</span>
              <input
                className={styles.input}
                type="number" step={step}
                value={form[key] ?? ''}
                onChange={e => setField(key, e.target.value)}
              />
              <span className={styles.fieldLabel} style={{ opacity: 0.6, fontWeight: 400 }}>{hint}</span>
            </label>
          ))}

          <button
            className={styles.chip}
            style={{ alignSelf: 'flex-start', marginTop: 4 }}
            onClick={() => setShowAdvanced(v => !v)}
          >{showAdvanced ? '▾ Advanced' : '▸ Advanced'}</button>

          {showAdvanced && (
            <>
              <label
                className={styles.field}
                title={'Rolling-resistance coefficient — tire + bearing drag (dimensionless). '
                  + 'Small effect overall (a few hp). ~0.010 hard street tires, ~0.013 typical, '
                  + '0.015–0.018 for 200TW / R-comps on a hot track.'}
              >
                <span className={styles.fieldLabel}>Rolling resistance (Crr)</span>
                <input
                  className={styles.input}
                  type="number" step={0.001}
                  value={form.crr ?? ''}
                  onChange={e => setField('crr', e.target.value)}
                />
              </label>
              <label
                className={styles.field}
                title={"Speed-smoothing window, in metres at the session's typical speed. "
                  + 'It is applied as a constant-time filter (so it spans more distance on '
                  + 'fast straights, less in slow corners) and stays consistent across all '
                  + 'laps. GPS speed is noisy and differentiating it amplifies that noise, '
                  + 'so larger = cleaner, more realistic power, at the cost of averaging out '
                  + 'brief transients. 50–80 m (≈1.5–2.5 s) suits most GPS data; clean '
                  + 'high-rate logs (50 Hz) can go lower.'}
              >
                <span className={styles.fieldLabel}>Smoothing (m @ typical speed)</span>
                <input
                  className={styles.input}
                  type="number" step={5}
                  value={form.smoothing_m ?? ''}
                  onChange={e => setField('smoothing_m', e.target.value)}
                />
                <span className={styles.fieldLabel} style={{ opacity: 0.6, fontWeight: 400 }}>
                  {impliedSec != null
                    ? `≈ ${impliedSec.toFixed(1)} s at typical speed (${Math.round(typicalKmh)} km/h)`
                    : 'Higher = smoother. Try 50–80 for GPS data.'}
                </span>
              </label>
              <label
                className={styles.field}
                style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}
                title={'Use road grade to account for hills — climbing costs power, '
                  + 'descending adds it (this is the largest correction the model makes: '
                  + 'tens of hp at a hilly circuit). Elevation comes from terrain data '
                  + '(USGS 3DEP lidar in the US, satellite elsewhere) sampled at each GPS '
                  + 'fix, falling back to the lap\'s own GPS altitude.'}
              >
                <input
                  type="checkbox"
                  checked={!!form.include_grade}
                  onChange={e => setFlag('include_grade', e.target.checked)}
                />
                <span className={styles.fieldLabel}>Include road grade (from terrain elevation)</span>
              </label>
            </>
          )}

          <div className={styles.field}>
            <span className={styles.fieldLabel} style={{ opacity: 0.6, fontWeight: 400 }}>
              Wheel power · air density from session weather
            </span>
          </div>

          {savedMsg && <div className={`${styles.preview} ${styles.previewOk}`}>{savedMsg}</div>}
          {error && <div className={styles.errorMsg}>⚠ {error}</div>}
        </div>

        <div className={styles.footer}>
          <button className={styles.cancelBtn} onClick={onClose} disabled={saving}>Close</button>
          <button className={styles.saveBtn} onClick={handleSave} disabled={saving || !canSave}
                  title={canSave ? '' : "Only the session owner can edit this car's params"}>
            {saving ? 'Saving…' : 'Save to car'}
          </button>
        </div>
      </div>
    </div>
  );
}
